import React, { useState, useEffect } from "react";
import { useBlogPosts } from "../../../../Hooks/useBlogPost";
import { useLocation, Link, useParams } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import { extensions } from "../../../../util/tiptapExtensions";
import styles from "./BlogPost.module.scss";
import "../../../../scss/TipTap/tiptap-global.scss";
import LoaderSpinner from "../../../../util/LoadingComponent/LoaderSpinner";
import PageSEO from "../../../../Components/SEO/PageSEO";

const BlogPost = ({ post: propPost }) => {
  const { posts, loading, message, error } = useBlogPosts();
  const location = useLocation();
  const { slug } = useParams();
  const [currentPost, setCurrentPost] = useState(propPost || location.state?.post);
  const [activeSection, setActiveSection] = useState(null);
  const [headings, setHeadings] = useState([]);

  const editor = useEditor({
    extensions,
    content: currentPost?.content || "",
    editable: false,
  });

  // Helper function to strip HTML tags for clean article body text
  const stripHtml = (html) => {
    if (!html) return '';
    return html
      .replace(/<[^>]*>/g, ' ') // Replace HTML tags with space
      .replace(/&nbsp;/g, ' ')  // Replace &nbsp; with space
      .replace(/\s+/g, ' ')     // Replace multiple spaces with single space
      .trim();                  // Remove leading/trailing spaces
  };

  // Update currentPost when URL changes
  useEffect(() => {
    if (slug && posts) {
      const newPost = posts.find(p => p.slug === slug);
      if (newPost) {
        setCurrentPost(newPost);
        if (editor) {
          editor.commands.setContent(newPost.content);
        }
      }
    }
  }, [slug, posts, editor]);

  // Define structured data for the individual blog post
  const blogPostSchema = currentPost ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": currentPost.title,
    "description": currentPost.excerpt,
    "datePublished": currentPost.createdAt,
    "dateModified": currentPost.createdAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://webnx.com/blog/${currentPost.slug}`
    },
    "publisher": {
      "@type": "Organization",
      "name": "WebNX",
      "logo": {
        "@type": "ImageObject",
        "url": "https://webnx.com/images/logo.png"
      }
    },
    "author": {
      "@type": "Organization",
      "name": "WebNX"
    },
    "articleBody": stripHtml(currentPost.content),
    "keywords": [
      "enterprise hosting",
      "dedicated servers",
      "data center infrastructure",
      currentPost.category || "hosting solutions",
      "WebNX insights"
    ],
    "inLanguage": "en-US",
    "isAccessibleForFree": "True"
  } : null;

  // Filter out the current post if needed, with proper checks
  const relatedPosts = posts && currentPost
    ? posts
        .filter((p) => p.slug !== currentPost.slug)
        .sort(() => 0.5 - Math.random())
        .slice(0, 2)
    : [];

  useEffect(() => {
    if (editor) {
      // Extract headings from content
      const doc = editor.view.dom;
      const headingElements = doc.querySelectorAll("h2, h3");
      const headingList = Array.from(headingElements).map((heading, index) => ({
        id: `section-${index}`,
        text: heading.textContent,
        level: heading.tagName.toLowerCase(),
      }));
      setHeadings(headingList);

      // Set up intersection observer for active section
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(entry.target.id);
            }
          });
        },
        { threshold: 0.5 }
      );

      headingElements.forEach((heading) => {
        observer.observe(heading);
      });

      return () => {
        headingElements.forEach((heading) => {
          observer.unobserve(heading);
        });
      };
    }
  }, [editor]);

  if (loading) return <LoaderSpinner />;
  if (error) return <div className={styles.error}>Error: {message}</div>;
  if (!currentPost) return <div className={styles.error}>Post not found</div>;

  return (
    <>
      <PageSEO
        title={`${currentPost.title} | WebNX Blog`}
        description={currentPost.excerpt}
        keywords={`${currentPost.category || ''}, enterprise hosting, dedicated servers, WebNX blog, hosting insights, ${currentPost.title.toLowerCase()}`}
        path={`/blog/${currentPost.slug}`}
        image="https://webnx.com/assets/blog-header.jpg"
        schema={blogPostSchema}
        socialProfiles={{
          facebook: "https://www.facebook.com/WebNX/",
          linkedin: "https://www.linkedin.com/company/webnx/"
        }}
      />

      <article className={styles.blogPost}>
        {/* <div className={styles.tableOfContents}>
          <h3>Table of Contents</h3>
          <ul>
            {headings.map((heading) => (
              <li key={heading.id}>
                <a
                  href={`#${heading.id}`}
                  className={activeSection === heading.id ? styles.active : ''}
                >
                  {heading.text}
                </a>
              </li>
            ))}
          </ul>
        </div> */}

        <div className={styles.blogContent}>
          <Link to="/blog" className={styles.backToBlog}>
            ← Back to Blog
          </Link>
          <EditorContent editor={editor} />

          {relatedPosts.length >= 1 && (
            <footer className={styles.blogFooter}>
              <div className={styles.footerContent}>
                <h3>You Might Also Like</h3>
                <div className={styles.relatedPosts}>
                  {relatedPosts.map((relatedPost) => (
                    <Link
                      key={relatedPost._id}
                      to={`/blog/${relatedPost.slug}`}
                      state={{ post: relatedPost }}
                      className={styles.postCard}
                    >
                      <div className={styles.cardContent}>
                        <div className={styles.cardHeader}>
                          <span className={styles.categoryTag}>
                            {relatedPost.category || 'General'}
                          </span>
                          <span className={styles.date}>
                            {new Date(relatedPost.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        <h4>{relatedPost.title}</h4>
                        <p>{relatedPost.excerpt}...</p>
                        <span className={styles.readMore}>
                          Read more
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link to="/blog" className={styles.footerLink}>
                  View all articles
                </Link>
              </div>
            </footer>
          )}
        </div>
      </article>
    </>
  );
};

export default BlogPost;
